import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosClient from '../services/api';
import LMSHeader from '../components/LMSHeader';


import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';


const CertificateVerifyPage = () => {
  const { certificateId } = useParams();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar: (Toolbar) => (
      <Toolbar>
        {(slots) => {
          const { ZoomIn, ZoomOut, SwitchTheme } = slots;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ZoomOut />
              <ZoomIn />
              <SwitchTheme />
            </div>
          );
        }}
      </Toolbar>
    ),
  });


  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const res = await axiosClient.get(`/certificates/verify/${certificateId}`);
        setCertificate(res.data.certificate);
      } catch (err) {
        setError('Failed to load certificate');
        console.error('Failed to fetch certificate:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCertificate();
  }, [certificateId]);

  if (isLoading) {
    return (
      <>
        <LMSHeader />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </>
    );
  }

  if (error || !certificate) {
    return (
      <>
        <LMSHeader />
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
          <div className="max-w-4xl mx-auto px-4">
            <button
              onClick={() => navigate(-1)}
              className="text-primary-600 dark:text-primary-400 hover:underline mb-4 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-8 text-center">
              <div className="text-6xl mb-4">📜</div>
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Certificate not found</h2>
              <p className="text-neutral-600 dark:text-neutral-400">
                The certificate ID you entered is invalid or the certificate has been revoked.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LMSHeader />
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="text-primary-600 dark:text-primary-400 hover:underline mb-4 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">Certificate</h1>
            <p className="text-neutral-600 dark:text-neutral-400 mt-2">
              View verified certificate details
            </p>
          </div>

          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Certificate Details</h2>
                <p className="text-neutral-600 dark:text-neutral-400 mt-1">
                  {certificate.course?.title}
                </p>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${certificate.status === 'issued'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  : certificate.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                    : certificate.status === 'revoked'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300'
                  }`}
              >
                {certificate.status
                  ? certificate.status.charAt(0).toUpperCase() + certificate.status.slice(1)
                  : 'Unknown'}
              </span>
            </div>

            {certificate.status === 'issued' && certificate.certificateUrl && (
              <div className="space-y-6">
                <div className="border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg overflow-hidden">
                  <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                    <Viewer fileUrl={`${process.env.REACT_APP_SOCKET_URL}/public${certificate.certificateUrl}`} plugins={[defaultLayoutPluginInstance]} />
                  </Worker>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CertificateVerifyPage;